import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { Car, Volume2, ArrowRight, MessageSquare } from 'lucide-react';

const CAR_VOCAB = [
  { de: 'das Auto', en: 'car' },
  { de: 'das Lenkrad', en: 'steering wheel' },
  { de: 'die Reifen', en: 'wheels / tyres' },
  { de: 'der Sitz', en: 'seat' },
  { de: 'der Tankdeckel', en: 'fuel tank cap' },
  { de: 'die Windschutzscheibe', en: 'windscreen' },
  { de: 'die Scheibenwischer', en: 'windscreen wipers' },
  { de: 'der Innenspiegel', en: 'rearview mirror' },
  { de: 'der Außenspiegel', en: 'side mirror' },
  { de: 'die Motorhaube', en: 'bonnet / hood' },
  { de: 'der Blinker', en: 'indicator / turn signal' },
  { de: 'der Auspuff', en: 'exhaust' },
];

const LIGHTS_VOCAB = [
  { de: 'das Fernlicht', en: 'high beam (the blue dashboard light)' },
  { de: 'das Abblendlicht', en: 'low beam' },
  { de: 'das Tagfahrlicht', en: 'daytime running lights' },
  { de: 'die Nebelschlussleuchte', en: 'rear fog light' },
];

const ROAD_VOCAB = [
  { de: 'links', en: 'left' },
  { de: 'rechts', en: 'right' },
  { de: 'der Kreisverkehr', en: 'roundabout' },
  { de: 'die Ampel', en: 'traffic light' },
  { de: 'der Parkplatz', en: 'parking lot / parking space' },
];

const COMMS = [
  { de: 'Biegen Sie bitte links ab.', en: 'Please turn left.' },
  { de: 'Biegen Sie bitte rechts ab.', en: 'Please turn right.' },
  { de: 'Bei dem Kreisverkehr bitte die dritte Ausfahrt nehmen.', en: 'Please take the third exit at the roundabout.' },
  { de: 'Wenn die Ampel gelb zeigt, sind wir bereit loszufahren.', en: 'When the traffic light is yellow, we are ready to go.' },
  { de: 'Parke bitte auf dem Parkplatz dieses Supermarkts.', en: 'Please park in the parking lot of this supermarket.' },
  { de: 'Kannst du das Fenster bitte öffnen?', en: 'Can you open the window, please?' },
];

const FuhrerscheinPage = () => {
  const [playingAudio, setPlayingAudio] = useState(null);

  const speak = (text, id) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.lang = 'de-DE';
      u.rate = 0.9;
      u.onstart = () => setPlayingAudio(id);
      u.onend = () => setPlayingAudio(null);
      window.speechSynthesis.speak(u);
    }
  };

  const renderGrid = (items, prefix) => (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {items.map((w, i) => (
        <button
          key={`${prefix}-${i}`}
          onClick={() => speak(w.de, `${prefix}-${i}`)}
          className="bg-babyblue-50 rounded-xl p-4 border border-babyblue-200 hover:border-primary hover:shadow-md transition-all text-left group flex items-start justify-between gap-3"
          data-testid={`${prefix}-vocab-${i}`}
        >
          <div>
            <p className="font-bold text-babyblue-800">{w.de}</p>
            <p className="text-sm text-babyblue-600">{w.en}</p>
          </div>
          <div className={`p-2 rounded-full flex-shrink-0 transition-all ${
            playingAudio === `${prefix}-${i}` ? 'bg-primary text-white' : 'bg-white text-primary opacity-0 group-hover:opacity-100'
          }`}>
            <Volume2 className="w-4 h-4" />
          </div>
        </button>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-babyblue-50 via-babyblue-100/50 to-white" data-testid="fuhrerschein-page">
      <Navbar />

      <section className="py-16 md:py-20 px-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-100/40 rounded-full filter blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="container mx-auto max-w-4xl relative z-10 text-center">
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Car className="w-4 h-4" />
            Integration Module
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-babyblue-800 mb-4">
            Der Führerschein
          </h1>
          <p className="text-xl text-babyblue-600 leading-relaxed max-w-3xl mx-auto">
            Struggling with German during your driving lessons? This is your survival vocabulary kit.
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-8 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-6">
            <p className="text-babyblue-700 leading-relaxed">
              The German <em>Führerschein</em> is famously rigorous and expensive (often €2,000–€4,000). This page focuses on the language side — the vocabulary and instructor phrases you'll hear in every driving lesson, so you know exactly what your <em>Fahrlehrer</em> expects.
            </p>
          </div>
        </div>
      </section>

      {/* Car parts vocab */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-5xl">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-babyblue-800 mb-2 text-center">Car Parts Vocabulary</h2>
          <p className="text-babyblue-600 text-center mb-8">Click any word to hear it pronounced.</p>
          {renderGrid(CAR_VOCAB, 'car')}
        </div>
      </section>

      {/* Lights vocab */}
      <section className="py-12 px-6 bg-white">
        <div className="container mx-auto max-w-5xl">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-babyblue-800 mb-2 text-center">Lights &amp; Signalling</h2>
          <p className="text-babyblue-600 text-center mb-8">Know which switch your instructor is yelling about.</p>
          {renderGrid(LIGHTS_VOCAB, 'light')}
        </div>
      </section>

      {/* Road vocab */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-5xl">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-babyblue-800 mb-2 text-center">Directions &amp; Road Features</h2>
          <p className="text-babyblue-600 text-center mb-8">Left, right, traffic lights and roundabouts.</p>
          {renderGrid(ROAD_VOCAB, 'road')}
        </div>
      </section>

      {/* Communication */}
      <section className="py-12 px-6 bg-gradient-to-b from-babyblue-50/40 to-white">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-babyblue-800 mb-2 text-center">Instructor Phrases</h2>
          <p className="text-babyblue-600 text-center mb-8 flex items-center justify-center gap-2">
            <MessageSquare className="w-4 h-4" />
            What you'll hear in every lesson
          </p>
          <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-6">
            <div className="space-y-2">
              {COMMS.map((phrase, i) => (
                <button
                  key={i}
                  onClick={() => speak(phrase.de, `comm-${i}`)}
                  className="w-full text-left bg-babyblue-50 hover:bg-babyblue-100 p-4 rounded-lg border border-babyblue-200 transition-all flex items-center justify-between"
                  data-testid={`fuhrerschein-comm-${i}`}
                >
                  <div>
                    <p className="font-semibold text-babyblue-800">{phrase.de}</p>
                    <p className="text-sm text-babyblue-600 italic">{phrase.en}</p>
                  </div>
                  <Volume2 className={`w-4 h-4 flex-shrink-0 ${playingAudio === `comm-${i}` ? 'text-primary animate-pulse' : 'text-babyblue-400'}`} />
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="font-heading text-xl font-bold text-babyblue-800 mb-6">Continue Your Integration</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {[
              { icon: '🏠', title: 'Finding Housing', path: '/integration/housing' },
              { icon: '🎓', title: 'Education & Training', path: '/integration/education' },
              { icon: '🤝', title: 'Daily Life & Culture', path: '/integration/daily-life' },
            ].map((item, i) => (
              <Link key={i} to={item.path} className="bg-babyblue-50 rounded-xl border border-babyblue-200 p-4 flex items-center gap-3 hover:border-primary hover:shadow-md transition-all">
                <span className="text-2xl">{item.icon}</span>
                <span className="text-babyblue-700 font-medium">{item.title}</span>
                <ArrowRight className="w-4 h-4 text-babyblue-400 ml-auto" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FuhrerscheinPage;
